import { WeekDay } from 'model';
import {
    resolveBookingType,
    resolveMaxPets,
    Service,
    SERVICES_BUSINESS_AREAS,
    SESSION_BOOKING_TYPES,
    SessionBookingType,
    usesSessions
} from 'model/Service';
import { Moment } from 'moment-timezone';

export type SessionBusinessArea = typeof SERVICES_BUSINESS_AREAS.WALKING | typeof SERVICES_BUSINESS_AREAS.TRAINING;

export const SESSION_AREA_LABELS: Record<SessionBusinessArea, string> = {
    walking: 'Walking',
    training: 'Training'
};

export type ServiceSessionRecurrence = 'weekly' | 'specific';

export interface SessionZoneMap {
    type: 'map';
    radius: number;
    latitude?: number;
    longitude?: number;
    address?: string;
}

export interface SessionZoneZipCodes {
    type: 'zip_codes';
    zipCodes: Array<string>;
}

export type SessionZone = SessionZoneMap | SessionZoneZipCodes;

export const SESSION_ZONE_REQUIRED_MESSAGE = 'Add a zone using the map or ZIP codes.';
export const SESSION_DUPLICATE_SLOT_MESSAGE = 'This staff member already has a session at this time.';

export interface ServiceSession {
    id?: number;
    uuid: string;
    serviceId: number;
    staffId?: number;
    staffName?: string;
    time: string;
    zone?: SessionZone;
    recurrence?: ServiceSessionRecurrence;
    weekDay?: WeekDay;
    sessionDate?: string;
}

export interface ServiceSessionDay {
    date: string;
    weekday: number;
    sessions: Array<ServiceSession>;
    cancellations: Array<ServiceSession>;
}

export interface SessionAssignmentError {
    staffId: number;
    time: string;
    messages: Array<string>;
}

export interface SessionAssignmentDraft {
    staffId: number;
    time: string;
    zone?: SessionZone;
}

export interface SessionSpecificAssignment {
    staffId: number;
    time: string;
    active?: boolean;
    zone?: SessionZone;
}

export interface ServiceSessionConfig {
    sessionType: SessionBookingType;
    maxPets: number;
    inHome: boolean;
    usesSessions: boolean;
}

export const WEEK_DAYS: Array<WeekDay> = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

export const WEEK_DAYS_FROM_MONDAY: Array<WeekDay> = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday'
];

export const getServiceSessionConfig = (service: Service, area: SessionBusinessArea): ServiceSessionConfig => {
    const metadata = area === SERVICES_BUSINESS_AREAS.TRAINING ? service.trainingData : service.walkingData;
    const sessionType = resolveBookingType(metadata?.bookingType);
    const inHome = !!metadata?.inHomeService;

    return {
        sessionType,
        inHome,
        maxPets: resolveMaxPets(metadata?.bookingType, metadata?.maxPets),
        usesSessions: usesSessions(sessionType, inHome)
    };
};

export const getSessionTypeLabel = (config: ServiceSessionConfig): string => {
    return config.sessionType === SESSION_BOOKING_TYPES.GROUP_SESSION ? 'Group Session' : 'Private Session';
};

export const getShortZoneAddress = (address?: string): string | undefined => {
    if (!address?.trim()) return undefined;

    const parts = address
        .split(',')
        .map(part => part.trim())
        .filter(Boolean);

    if (parts.length > 1 && /^(usa|us|united states)$/i.test(parts[parts.length - 1])) {
        parts.pop();
    }

    return parts.slice(0, 2).join(', ') || undefined;
};

export const getZoneLabel = (zone?: SessionZone): string | undefined => {
    if (!zone) return undefined;

    if (zone.type === 'zip_codes') return zone.zipCodes.join(', ');

    const miles = zone.radius === 1 ? '1 mile' : `${zone.radius} miles`;
    const around = getShortZoneAddress(zone.address);

    if (around) return `${miles} around ${around}`;

    return zone.latitude !== undefined && zone.longitude !== undefined
        ? `${miles} around selected location`
        : `Map area · ${miles}`;
};

export const dateKey = (date: Moment): string => date.format('YYYY-MM-DD');

export const weekDayOf = (date: Moment): WeekDay => WEEK_DAYS[date.day()];

export const weekDayIndex = (weekDay: WeekDay): number => WEEK_DAYS.indexOf(weekDay);

export const zonesEqual = (left?: SessionZone, right?: SessionZone): boolean => {
    if (!left && !right) return true;
    if (!left || !right || left.type !== right.type) return false;

    if (left.type === 'zip_codes' && right.type === 'zip_codes') {
        return left.zipCodes.join(',') === right.zipCodes.join(',');
    }

    if (left.type === 'map' && right.type === 'map') {
        return left.radius === right.radius && left.latitude === right.latitude && left.longitude === right.longitude;
    }

    return false;
};

export const sessionAssignmentKey = (staffId: number, time: string): string => `${staffId}|${time.slice(0, 5)}`;

export const isSameSessionAssignment = (
    left: { staffId?: number; time?: string },
    right: { staffId?: number; time?: string }
): boolean => {
    return (
        !!left.staffId &&
        !!right.staffId &&
        !!left.time &&
        !!right.time &&
        sessionAssignmentKey(left.staffId, left.time) === sessionAssignmentKey(right.staffId, right.time)
    );
};

const bySlotOrder = (left: ServiceSession, right: ServiceSession): number =>
    left.time.localeCompare(right.time) || (left.staffName ?? '').localeCompare(right.staffName ?? '');

export const getSessionsForDate = (
    days: Array<ServiceSessionDay>,
    serviceId: number,
    date: Moment
): Array<ServiceSession> => {
    const day = days.find(item => item.date === dateKey(date));

    return (day?.sessions.filter(session => session.serviceId === serviceId) ?? []).slice().sort(bySlotOrder);
};

export const getWeeklySessionsForWeekDay = (
    days: Array<ServiceSessionDay>,
    serviceId: number,
    weekDay: WeekDay
): Array<ServiceSession> => {
    const weekliesOf = (day: ServiceSessionDay) =>
        day.sessions.filter(session => session.serviceId === serviceId && session.recurrence === 'weekly');

    // A fully cancelled occurrence has no weekly rows, so the copy with the most weeklies wins.
    return days
        .filter(item => WEEK_DAYS[item.weekday] === weekDay)
        .reduce<Array<ServiceSession>>((best, day) => {
            const weeklies = weekliesOf(day);
            return weeklies.length > best.length ? weeklies : best;
        }, [])
        .slice()
        .sort(bySlotOrder);
};

export const getCancellationsForDate = (
    days: Array<ServiceSessionDay>,
    serviceId: number,
    date: Moment
): Array<ServiceSession> => {
    const day = days.find(item => item.date === dateKey(date));
    const weeklyKeys = new Set(
        getWeeklySessionsForWeekDay(days, serviceId, weekDayOf(date))
            .filter(session => session.staffId)
            .map(session => sessionAssignmentKey(session.staffId!, session.time))
    );
    const activeKeys = new Set(
        getSessionsForDate(days, serviceId, date)
            .filter(session => session.staffId)
            .map(session => sessionAssignmentKey(session.staffId!, session.time))
    );

    return (day?.cancellations ?? []).filter(session => {
        if (session.serviceId !== serviceId || !session.staffId) return false;

        const key = sessionAssignmentKey(session.staffId, session.time);
        if (activeKeys.has(key)) return false;

        return weeklyKeys.has(key);
    });
};

export const groupSessionsByTime = (sessions: Array<ServiceSession>): Array<{ time: string; staffCount: number }> => {
    const counts = new Map<string, number>();

    sessions.forEach(session => {
        counts.set(session.time, (counts.get(session.time) ?? 0) + 1);
    });

    return Array.from(counts.entries())
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([time, staffCount]) => ({ time, staffCount }));
};

const matchesSlot = (session: ServiceSession, draft: SessionAssignmentDraft): boolean => {
    return session.staffId === draft.staffId && session.time === draft.time;
};

const matchesDraft = (session: ServiceSession, draft: SessionAssignmentDraft): boolean => {
    return matchesSlot(session, draft) && zonesEqual(session.zone, draft.zone);
};

export const duplicateAssignmentKeys = (
    assignments: Array<{ key: string; staffId?: number; time?: string }>
): Set<string> => {
    const seen = new Set<string>();
    const duplicates = new Set<string>();

    assignments.forEach(item => {
        if (!item.staffId || !item.time) return;

        const slot = sessionAssignmentKey(item.staffId, item.time);
        if (seen.has(slot)) duplicates.add(item.key);
        seen.add(slot);
    });

    return duplicates;
};

export const buildSpecificAssignments = (
    desired: Array<SessionAssignmentDraft>,
    livingWeeklies: Array<ServiceSession> = [],
    cancellations: Array<ServiceSession> = []
): Array<SessionSpecificAssignment> => {
    const assignments: Array<SessionSpecificAssignment> = [];
    const revivals: Array<SessionSpecificAssignment> = [];
    const cancelledKeys = new Set(
        cancellations
            .filter(session => session.staffId)
            .map(session => sessionAssignmentKey(session.staffId!, session.time))
    );

    livingWeeklies.forEach(session => {
        if (!session.staffId) return;
        // A specific on the same slot already replaces the weekly; cancelling it too would contradict it.
        if (desired.some(draft => matchesSlot(session, draft))) return;

        assignments.push({
            staffId: session.staffId,
            time: session.time,
            active: false
        });
    });

    desired.forEach(draft => {
        if (livingWeeklies.some(session => matchesDraft(session, draft))) {
            if (cancelledKeys.has(sessionAssignmentKey(draft.staffId, draft.time))) {
                revivals.push({
                    staffId: draft.staffId,
                    time: draft.time,
                    zone: draft.zone,
                    active: true
                });
            }
            return;
        }

        assignments.push({
            staffId: draft.staffId,
            time: draft.time,
            zone: draft.zone
        });
    });

    // An empty payload already wipes the specific layer, cancellations included.
    return assignments.length > 0 ? [...assignments, ...revivals] : [];
};

export const draftsMatchSessions = (
    drafts: Array<SessionAssignmentDraft>,
    sessions: Array<ServiceSession>
): boolean => {
    if (drafts.length !== sessions.length) return false;

    return drafts.every(draft => sessions.some(session => matchesDraft(session, draft)));
};

export const warningsForActiveAssignments = (
    warnings: Array<string>,
    activeStaffNames: Array<string>
): Array<string> => {
    if (activeStaffNames.length === 0) return [];

    const names = new Set(activeStaffNames);

    return warnings.filter(warning => {
        const normalized = warning.replace(/[\u2018\u2019]/g, "'");
        const match = normalized.match(/^Conflicts with (.+)'s working hours\.?$/i);
        if (!match) return true;
        return names.has(match[1]);
    });
};
